import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import { browseHrefForKey } from '../../lib/wiki';
import type {
  ProjectDto,
  StateDto,
  WorkItemFull,
  WorkItemSubtypeDefinition,
} from '../../lib/work-types';
import { cn } from '../../lib/utils';
import { PriorityLabel, StateLabel, SubtypeLabel, TypeLabel } from '../work/table-cells';

type CardState =
  | { status: 'loading' }
  | {
      status: 'ready';
      item: WorkItemFull;
      state: StateDto | null;
      subtypes: WorkItemSubtypeDefinition[];
    }
  | { status: 'error'; message: string; notFound: boolean };

const statesByProject = new Map<string, Promise<StateDto[]>>();

function loadStates(projectId: string): Promise<StateDto[]> {
  const cached = statesByProject.get(projectId);
  if (cached) return cached;
  const pending = apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`))
    .then((res) => {
      if (!res || !Array.isArray(res.items)) throw new Error('Malformed states response.');
      return res.items;
    })
    .catch((err: unknown) => {
      statesByProject.delete(projectId);
      throw err;
    });
  statesByProject.set(projectId, pending);
  return pending;
}

/**
 * Live work-item embed (wiki.md W-10). The Markdown stays a plain link to
 * `/browse/<KEY>`; this card is only how the client renders it.
 */
export default function WorkItemCard({ workItemKey, className }: { workItemKey: string; className?: string }) {
  const [state, setState] = useState<CardState>({ status: 'loading' });
  const href = browseHrefForKey(workItemKey);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void apiFetch<WorkItemFull>(v1Path(`/work-items/${encodeURIComponent(workItemKey)}`))
      .then(async (item) => {
        const [states, project] = await Promise.all([
          loadStates(item.project_id),
          apiFetch<ProjectDto>(v1Path(`/projects/${item.project_id}`)),
        ]);
        if (cancelled) return;
        setState({
          status: 'ready',
          item,
          state: states.find((s) => s.id === item.state_id) ?? null,
          subtypes: project.subtypes ?? [],
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const notFound = err instanceof ApiError && (err.status === 404 || err.status === 403);
        setState({ status: 'error', message: apiErrorMessage(err, 'Failed to load work item'), notFound });
      });
    return () => {
      cancelled = true;
    };
  }, [workItemKey]);

  const shell = cn(
    'my-2 flex items-center gap-3 rounded-md border bg-card px-3 py-2 text-sm no-underline shadow-sm',
    'hover:bg-accent/40',
    className,
  );

  if (state.status === 'loading') {
    return (
      <div className={shell} aria-busy="true">
        <span className="font-mono text-xs text-muted-foreground">{workItemKey}</span>
        <span className="text-muted-foreground">Loading…</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <Link to={href} className={shell} title={state.message}>
        <span className="font-mono text-xs text-muted-foreground">{workItemKey}</span>
        <span className="text-muted-foreground">
          {state.notFound ? 'Not found or no access' : state.message}
        </span>
      </Link>
    );
  }

  const { item } = state;
  return (
    <Link
      to={`/projects/${item.project_id}/work-items/${item.id}`}
      className={shell}
      data-work-item-key={item.key}
    >
      <span className="shrink-0 font-mono text-xs text-muted-foreground">{item.key}</span>
      <span className={cn('min-w-0 flex-1 truncate font-medium', item.archived_at && 'line-through')}>
        {item.title}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <TypeLabel type={item.type} />
        <SubtypeLabel subtype={item.subtype} subtypes={state.subtypes} />
        <PriorityLabel priority={item.priority} />
        {state.state ? <StateLabel name={state.state.name} color={state.state.color} group={state.state.group} /> : null}
      </span>
    </Link>
  );
}
